import type { SeoMeta } from "./meta";

const JSON_LD_ID = "zoniq-seo-jsonld";

type MetaAttr = "name" | "property";

function upsertMeta(attr: MetaAttr, key: string, content: string): void {
  if (!content) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string): void {
  if (!href) return;
  const selector = `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeJsonLd(): void {
  document.getElementById(JSON_LD_ID)?.remove();
}

function injectJsonLd(schemas: Record<string, unknown>[]): void {
  removeJsonLd();
  if (schemas.length === 0) return;

  const script = document.createElement("script");
  script.id = JSON_LD_ID;
  script.type = "application/ld+json";
  script.text = JSON.stringify(schemas.length === 1 ? schemas[0] : { "@context": "https://schema.org", "@graph": schemas });
  document.head.appendChild(script);
}

export type ApplySeoHeadOptions = {
  meta: SeoMeta;
  canonicalUrl: string;
  ogImageUrl: string;
  jsonLd?: Record<string, unknown>[];
};

/** Apply document title, meta tags, canonical, and optional JSON-LD. */
export function applySeoHead({ meta, canonicalUrl, ogImageUrl, jsonLd = [] }: ApplySeoHeadOptions): void {
  document.title = meta.title;

  upsertMeta("name", "description", meta.description);
  upsertMeta("name", "robots", meta.robots);
  upsertMeta("name", "author", "Zoniq");

  upsertMeta("property", "og:title", meta.title);
  upsertMeta("property", "og:description", meta.description);
  upsertMeta("property", "og:type", meta.ogType);
  upsertMeta("property", "og:site_name", "Zoniq EventOS");
  upsertMeta("property", "og:locale", "en_IN");
  if (canonicalUrl) {
    upsertMeta("property", "og:url", canonicalUrl);
  }
  if (ogImageUrl) {
    upsertMeta("property", "og:image", ogImageUrl);
    upsertMeta("property", "og:image:alt", "Zoniq EventOS — event operations platform");
  }

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", meta.title);
  upsertMeta("name", "twitter:description", meta.description);
  if (ogImageUrl) {
    upsertMeta("name", "twitter:image", ogImageUrl);
  }

  upsertLink("canonical", canonicalUrl);

  if (meta.structuredData && jsonLd.length > 0) {
    injectJsonLd(jsonLd);
  } else {
    removeJsonLd();
  }
}

export function cleanupSeoHead(): void {
  removeJsonLd();
}
